// ds-color-ignore-file: system font stack in dev/internal switcher
"use client"

import { useEffect, useState } from "react"
import { Cockpit } from "@/components/editor/cockpit"
import { DEV_PERSONAS } from "@/lib/edit/dev-personas"

const STORAGE_KEY = "ahead-dev-act-as"

/**
 * Dev-only "use it as <account>" switcher. Renders nothing unless
 * `NEXT_PUBLIC_DEV_EDITOR_IMPERSONATION=true` (build-time inlined, never set on a
 * deployed build). Lets you dogfood the inline editor + review cockpit as any
 * seeded persona without a real Google login: it mounts the same right-docked
 * `Cockpit` with a synthetic `dev:<email>` idToken, which the API routes' dev
 * path (`resolveEditorIdentity` + `DEV_EDITOR_IMPERSONATION`) trusts only in dev.
 *
 * This is the second half of "both" auth paths — real Firebase SSO still works
 * through the ahead-core FeedbackToolbar; this is the no-login impersonation lane.
 */
export function DevEditorSwitcher() {
  const enabled = process.env.NEXT_PUBLIC_DEV_EDITOR_IMPERSONATION === "true"
  const [email, setEmail] = useState<string | null>(null)
  const [open, setOpen] = useState(false)

  // Persist the active persona so a page navigation keeps you "logged in".
  useEffect(() => {
    if (!enabled) return
    const saved = window.localStorage.getItem(STORAGE_KEY)
    if (saved) setEmail(saved)
  }, [enabled])

  function selectPersona(next: string | null) {
    setEmail(next)
    setOpen(false)
    if (next) window.localStorage.setItem(STORAGE_KEY, next)
    else window.localStorage.removeItem(STORAGE_KEY)
  }

  if (!enabled) return null

  const active = DEV_PERSONAS.find((p) => p.email === email) ?? null

  return (
    <>
      <div
        style={{
          position: "fixed",
          bottom: 12,
          left: 12,
          zIndex: 2147483647,
          fontFamily: "ui-sans-serif, system-ui",
          fontSize: 12,
        }}
      >
        <button
          onClick={() => setOpen((v) => !v)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            border: "1px solid var(--color-deep-green-200, #b7d8c0)",
            background: active
              ? "var(--color-deep-green-600, #2f8f5b)"
              : "var(--color-neutral-00, #fff)",
            color: active ? "#fff" : "var(--color-deep-green-700, #1f6b43)",
            borderRadius: 9,
            padding: "6px 11px",
            fontWeight: 700,
            cursor: "pointer",
            boxShadow: "0 6px 18px rgba(0,0,0,.14)",
          }}
        >
          <span aria-hidden>🧪</span>
          {active ? `Dev: ${active.label}` : "Dev: not signed in"}
          <span aria-hidden style={{ opacity: 0.7 }}>
            ▾
          </span>
        </button>

        {open && (
          <div
            style={{
              marginTop: 6,
              background: "var(--color-neutral-00, #fff)",
              border: "1px solid var(--color-deep-green-200, #b7d8c0)",
              borderRadius: 11,
              padding: 6,
              minWidth: 280,
              boxShadow: "0 10px 30px rgba(0,0,0,.18)",
            }}
          >
            <div
              style={{
                padding: "5px 8px 7px",
                fontSize: 11,
                fontWeight: 700,
                color: "var(--color-neutral-500, #737373)",
              }}
            >
              Use it as…
            </div>
            {DEV_PERSONAS.map((p) => (
              <button
                key={p.email}
                onClick={() => selectPersona(p.email)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  border: "none",
                  background:
                    p.email === email
                      ? "var(--color-deep-green-50, #e8f5ee)"
                      : "transparent",
                  borderRadius: 8,
                  padding: "7px 8px",
                  cursor: "pointer",
                }}
              >
                <div style={{ fontWeight: 700, color: "#171717" }}>
                  {p.label}
                </div>
                <div style={{ color: "#737373", marginTop: 1 }}>{p.note}</div>
              </button>
            ))}
            {email && (
              <button
                onClick={() => selectPersona(null)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  border: "none",
                  background: "transparent",
                  color: "var(--color-terracotta-500, #c0512b)",
                  borderRadius: 8,
                  padding: "7px 8px",
                  marginTop: 2,
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                Sign out
              </button>
            )}
          </div>
        )}
      </div>

      {active && (
        <Cockpit
          key={active.email}
          active
          identity={{
            idToken: `dev:${active.email}`,
            environment: "development",
            branch: process.env.NEXT_PUBLIC_AHEAD_BRANCH,
          }}
          // Dev path: leaving edit mode deselects the persona (unmounts the cockpit).
          onExit={() => selectPersona(null)}
        />
      )}
    </>
  )
}
